import './ProductsFilter.scss'

import {DownloadCard, DownloadType} from '../download/DownloadCard'
import {Filter, FilterEmpty, FilterType} from 'views/ui/Filter'
import {ProductTaxonomyType, ProductType} from '../../pages'
import {useHistory, useLocation} from 'react-router'

import {ProductCardPreview} from './ProductCard'
import React from 'react'
import {ScrollMemory} from '../../../../util/scrollmemory'
import {Theme} from 'views/Theme'
import {parse} from 'query-string'
import {style} from 'util/style'
import {useTranslation} from 'util/i18n'

const GROUPKEY = 'productgroup'
const FILTERKEY = 'productfilter'
const styles = style('productsfilter')

type ProductsFilterType = {
	filters: FilterType
	groups: FilterType
	products?: Array<ProductType> | 'hide'
	downloads?: Array<DownloadType> | 'hide'
	maxItems?: number
	productsOverviewTitle?: string
	productsLoadmoreLink?: string
	downloadsOverviewTitle?: string
	downloadsLoadmoreLink?: string
}

export const ProductsFilter: React.FC<ProductsFilterType> = ({
	filters,
	groups,
	products,
	downloads,
	maxItems = null,
	productsOverviewTitle,
	productsLoadmoreLink,
	downloadsOverviewTitle,
	downloadsLoadmoreLink
}) => {
	const t = useTranslation('webshop').filter
	const location = useLocation()
	const history = useHistory()
	const hideProducts = products === 'hide'
	const hideDownloads = downloads === 'hide'
	const hasProducts = products?.length > 0
	const hasDownloads = downloads?.length > 0
	const hasFilters = filters?.length > 0
	const hasGroups = groups?.length > 0

	const locationSearch = parse(location.search)
	const chosenGroup = locationSearch[GROUPKEY]
	const chosenGroupData =
		hasGroups && groups.find(({path}) => path === chosenGroup)

	const chosenFilter = locationSearch[FILTERKEY]
	const chosenFilterData =
		hasFilters && filters.find(({path}) => path === chosenFilter)

	const filterItems = (items?: Array<ProductType | DownloadType>) => {
		return items.filter(
			({product_groups: productgroups, product_filters: productfilters}) => {
				const hasProductGroups = productgroups?.length > 0
				const hasFilterGroups = productfilters?.length > 0

				const noFilter = !chosenFilterData && !chosenGroupData
				const productDoesntHaveFilters = !hasProductGroups && !hasFilterGroups

				if (noFilter) return true
				if (productDoesntHaveFilters) return !!noFilter

				const onlyProductGroups = !chosenFilterData && chosenGroupData
				const onlyFilterGroups = chosenFilterData && !chosenGroupData
				const groupsAndFilters = chosenGroupData && chosenFilterData
				if (
					onlyProductGroups &&
					hasProductGroups &&
					productgroups.map((el) => Number(el)).includes(chosenGroupData?.id)
				)
					return true
				if (
					onlyFilterGroups &&
					hasFilterGroups &&
					productfilters.map((el) => Number(el)).includes(chosenFilterData?.id)
				)
					return true
				if (
					groupsAndFilters &&
					hasProductGroups &&
					productgroups.map((el) => Number(el)).includes(chosenGroupData?.id) &&
					hasFilterGroups &&
					productfilters.map((el) => Number(el)).includes(chosenFilterData?.id)
				)
					return true

				return false
			}
		)
	}

	const sliceItems = (items?: Array<ProductType | DownloadType>) => {
		if (!items) return null
		if (!maxItems) return items
		return items.slice(0, maxItems)
	}
	const filteredProducts =
		!hideProducts && hasProducts && filterItems(products as Array<ProductType>)
	const filteredDownloads =
		!hideDownloads &&
		hasDownloads &&
		filterItems(downloads as Array<DownloadType>)

	const slicedProducts = sliceItems(filteredProducts)
	const slicedDownloads = sliceItems(filteredDownloads)

	const hasFilteredProducts = filteredProducts && filteredProducts?.length > 0
	const hasFilteredDownloads =
		filteredDownloads && filteredDownloads?.length > 0
	const reset = t.reset

	return (
		<div className={styles}>
			{hasGroups && (
				<div className={styles('filters')}>
					<Theme.Container>
						<div className={styles('filters-container')}>
							<Filter label={t.label} filterKey={GROUPKEY} filters={groups} />
							<Filter filterKey={FILTERKEY} filters={filters} />
							<div className={styles('filters-container-reset')}>
								<Theme.Link
									href="#"
									onClick={(e) => {
										e.preventDefault()
										ScrollMemory.IGNORE_SCROLL = true

										history.push({
											pathname: location.pathname,
											search: null
										})
									}}
								>
									{reset}
								</Theme.Link>
							</div>
						</div>
					</Theme.Container>
				</div>
			)}
			<Theme.Container>
				{!hideProducts && (
					<div className={styles('items').mod('products')}>
						{productsOverviewTitle && (
							<Theme.H2>{productsOverviewTitle}</Theme.H2>
						)}
						{!hasFilteredProducts && <FilterEmpty>{t.no_products}</FilterEmpty>}
						{hasFilteredProducts && (
							<ProductsFilterGrid>
								{slicedProducts.map(
									(product: ProductType & ProductTaxonomyType, i: number) => (
										<ProductsFilterGridItem key={product.path}>
											<ProductCardPreview featured={i === 3} {...product} />
										</ProductsFilterGridItem>
									)
								)}
							</ProductsFilterGrid>
						)}
						{hasFilteredProducts && productsLoadmoreLink && (
							<div className={styles('loadmore')}>
								<Theme.Button to={productsLoadmoreLink + location?.search}>
									{t.load_more_products}
								</Theme.Button>
							</div>
						)}
					</div>
				)}
				{!hideDownloads && (
					<div className={styles('items').mod('downloads')}>
						{downloadsOverviewTitle && (
							<Theme.H2>{downloadsOverviewTitle}</Theme.H2>
						)}
						{!hasFilteredDownloads && (
							<FilterEmpty>{t.no_downloads}</FilterEmpty>
						)}
						{hasFilteredDownloads && (
							<ProductsFilterGrid>
								{slicedDownloads.map(
									(download: DownloadType, i: number) =>
										download?.download_file && (
											<ProductsFilterGridItem key={download.path}>
												<DownloadCard {...download} />
											</ProductsFilterGridItem>
										)
								)}
							</ProductsFilterGrid>
						)}
						{hasFilteredDownloads && downloadsLoadmoreLink && (
							<div className={styles('loadmore')}>
								<Theme.Button to={downloadsLoadmoreLink + location?.search}>
									{t.load_more_downloads}
								</Theme.Button>
							</div>
						)}
					</div>
				)}
			</Theme.Container>
		</div>
	)
}
export const ProductsFilterGrid: React.FC = ({children}) => {
	if (!children || (Array.isArray(children) && children.length === 0))
		return null
	return <div className={styles('grid')}>{children}</div>
}

export const ProductsFilterGridItem: React.FC = ({children}) => (
	<div className={styles('grid-item')}>{children}</div>
)
